import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { PauseCircle, Volume2, VolumeX, WifiOff } from "lucide-react";
import { sfx } from "@/lib/sfx";
import { Button } from "@/components/ui/button";
import { useRemaining, useRoomSocket } from "@/lib/ws";
import { TOKEN_KEY } from "@/lib/api";
import { getMode } from "@/modes";
import { ControlBar } from "@/components/game/ControlBar";
import { LobbyBoard } from "@/components/game/LobbyBoard";
import { CelebrationMusic } from "@/components/game/CelebrationMusic";
import { BoardAlerts } from "@/components/game/BoardAlerts";
import { BadgeButton } from "@/components/game/BadgeCard";
import { Logo } from "@/components/Logo";

function useBoardSounds(state, remaining) {
  const sec = Math.ceil(remaining / 1000);
  const phaseRef = useRef(null);
  const on = state?.settings?.sound !== false;
  useEffect(() => {
    if (on && state?.phase === "question" && !state.paused && sec > 0 && sec <= 5) sfx.tick(sec <= 2);
  }, [sec, on, state?.phase, state?.paused]);
  useEffect(() => {
    const key = `${state?.phase}-${state?.q_index}`;
    if (on && state?.phase === "results" && phaseRef.current && phaseRef.current !== key && !state.results?.skipped) sfx.result();
    phaseRef.current = key;
  }, [state?.phase, state?.q_index, on, state?.results?.skipped]);
  const drawRef = useRef(null);
  useEffect(() => {
    const n = state?.mode === "bingo" && state?.phase === "activity" ? state.drawn_count : null;
    if (on && n && drawRef.current != null && n > drawRef.current) sfx.draw();
    drawRef.current = n;
  }, [state?.mode, state?.phase, state?.drawn_count, on]);
}

const SoundButton = ({ on, setOn }) => {
  if (on) {
    return (
      <Button variant="outline" onClick={() => { sfx.disable(); setOn(false); }} className="h-12 px-5 text-base" title="Sesi kapat" data-testid="disable-sound-button">
        <Volume2 className="h-5 w-5 mr-2 text-[#34D399]" data-testid="board-sound-on" />Sesi Kapat
      </Button>
    );
  }
  return (
    <Button onClick={() => { sfx.enable(); sfx.result(); setOn(true); }} className="h-12 px-5 text-base" data-testid="enable-sound-button">
      <VolumeX className="h-5 w-5 mr-2" />Sesi Etkinleştir
    </Button>
  );
};

export default function BoardPage() {
  const { code } = useParams();
  const token = localStorage.getItem(TOKEN_KEY);
  const { state, connected, fatal, send, deadline } = useRoomSocket(`/board/${code}?token=${encodeURIComponent(token || "")}`);
  const remaining = useRemaining(state, deadline);
  useBoardSounds(state, remaining);
  const [soundOn, setSoundOn] = useState(sfx.enabled);

  if (fatal) return <p className="p-10 text-3xl" data-testid="board-error">Tahta açılamadı ({fatal === "unauthorized" ? "öğretmen girişi gerekli" : "oyun bulunamadı"}).</p>;
  if (!state) return <p className="p-10 eyebrow text-xl" data-testid="board-connecting">Bağlanılıyor…</p>;
  const Mode = getMode(state.mode);

  return (
    <div className={`h-screen flex flex-col px-[2.5vw] py-[2.5vh] gap-[2vh] overflow-hidden board-bg ${state.settings?.animations === false ? "no-anim" : ""}`} data-testid="board-page">
      <header className="flex items-center gap-6 shrink-0">
        <Logo />
        <span className="eyebrow text-base">{state.mode_label}</span>
        {state.phase !== "lobby" && <span className="ml-auto font-display text-2xl text-slate-300">Kod: <b className="text-[#FACC15] tracking-[0.08em]">{state.code}</b></span>}
        {state.phase === "lobby" && <span className="ml-auto" />}
        {!connected && <WifiOff className="h-7 w-7 text-red-400" />}
        <BoardAlerts state={state} />
        {state.badge && <BadgeButton badge={state.badge} />}
        <SoundButton on={soundOn} setOn={setSoundOn} />
      </header>
      <main className="flex-1 min-h-0 overflow-hidden relative">
        {state.phase === "lobby" ? <LobbyBoard state={state} /> : <Mode.BoardView state={state} remaining={remaining} send={send} />}
        {state.paused && (
          <div className="absolute inset-0 bg-[#0B0F16]/85 grid place-items-center" data-testid="board-paused">
            <div className="flex items-center gap-6"><PauseCircle className="h-24 w-24 text-[#FACC15]" /><span className="font-display text-8xl font-black">Duraklatıldı</span></div>
          </div>
        )}
        {state.phase === "finished" && soundOn && state.settings?.sound !== false && state.settings?.celebration_music !== false && <CelebrationMusic />}
      </main>
      <footer className="border-t border-slate-500/20 pt-[1.5vh] shrink-0"><ControlBar state={state} send={send} big /></footer>
    </div>
  );
}
